// Content for the /pages/:slug lead-magnet landing pages.
// Each entry is a fully-structured, SEO-oriented sales page. The icon fields are
// string keys mapped to lucide-react icons in src/pages/LeadMagnet.tsx.

export type LeadPillar = { icon: string; title: string; body: string };
export type LeadCompareRow = { label: string; left: string; right: string; leftPositive?: boolean };
export type LeadStep = { title: string; body: string };
export type LeadFaq = { q: string; a: string };
export type LeadStat = { value: string; label: string };

export interface LeadMagnet {
  slug: string;
  eyebrow: string;
  /** Headline. Wrap the part to gradient-highlight in [[double brackets]]. */
  title: string;
  subtitle: string;
  metaTitle: string;
  metaDescription: string;

  stats: LeadStat[];

  problemKicker: string;
  problemTitle: string;
  problemBody: string;
  problemPoints: string[];

  pillarsKicker: string;
  pillarsTitle: string;
  pillars: LeadPillar[];

  compareKicker: string;
  compareTitle: string;
  compareLeftLabel: string;
  compareRightLabel: string;
  compareRows: LeadCompareRow[];

  processKicker: string;
  processTitle: string;
  steps: LeadStep[];

  faqKicker: string;
  faqTitle: string;
  faqs: LeadFaq[];

  ctaTitle: string;
  ctaBody: string;

  /**
   * Ad landing pages flip the hero to a low-friction, blueprint-first CTA
   * (instant value, no form) with "talk to us" as the secondary action.
   */
  adLanding?: boolean;
}

export const leadMagnets: LeadMagnet[] = [
  {
    slug: "build-your-own-tool-instead-of-saas",
    eyebrow: "// own_it",
    title: "Own your tool. [[Stop renting your business.]]",
    subtitle:
      "Every month you pay for SaaS you don't control — its roadmap, its price hikes, its limits. We build software you own outright: shaped around how you actually work, free to evolve, and yours forever.",
    metaTitle: "Build Your Own Tool Instead of Paying for SaaS | DevCore",
    metaDescription:
      "Tired of SaaS limits, per-seat pricing and someone else's roadmap? DevCore builds custom software you own — flexible, scalable and shaped around your business.",
    stats: [
      { value: "100%", label: "ownership of code & data" },
      { value: "0", label: "per-seat fees as you grow" },
      { value: "∞", label: "features you can add" },
    ],
    problemKicker: "// the_trap",
    problemTitle: "SaaS feels cheap — until it owns your business",
    problemBody:
      "Off-the-shelf tools start convenient and end up controlling you. You bend your process to fit their software, pay more every time you add a person, and wait for features that may never ship. The subscription never ends, and you never own a thing.",
    problemPoints: [
      "Per-seat pricing that punishes you for growing",
      "Critical features stuck on a roadmap you can't influence",
      "Your data locked inside someone else's platform",
      "Workarounds, spreadsheets and duct tape to fill the gaps",
      "Surprise price increases with zero alternatives",
    ],
    pillarsKicker: "// why_own",
    pillarsTitle: "What ownership actually gives you",
    pillars: [
      {
        icon: "Lock",
        title: "Real ownership",
        body: "You own the code, the data and the infrastructure. No vendor lock-in, no platform that can pull the rug, no annual ransom to keep working.",
      },
      {
        icon: "SlidersHorizontal",
        title: "Built around you",
        body: "The software fits your exact workflow — not a generic average of everyone else's. Every screen, field and rule reflects how your team really operates.",
      },
      {
        icon: "TrendingUp",
        title: "Scales for free",
        body: "Add 5 users or 500 — your cost doesn't balloon. You pay for building and hosting, never a tax on every new seat.",
      },
      {
        icon: "Puzzle",
        title: "Endlessly adaptable",
        body: "New idea on Monday, live by Friday. Your tool changes when your business changes, instead of waiting on a vendor's queue.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Renting SaaS vs. owning your tool",
    compareLeftLabel: "Generic SaaS",
    compareRightLabel: "Your custom tool",
    compareRows: [
      { label: "Pricing", left: "Per seat, forever", right: "Build once, own it" },
      { label: "Fit", left: "You adapt to it", right: "It adapts to you" },
      { label: "Roadmap", left: "Someone else decides", right: "You decide" },
      { label: "Data", left: "Locked in their cloud", right: "Yours, anywhere" },
      { label: "Scaling cost", left: "Grows with headcount", right: "Effectively flat" },
      { label: "Integrations", left: "Only what they allow", right: "Anything you need" },
    ],
    processKicker: "// how_we_build",
    processTitle: "From subscription chaos to owned software",
    steps: [
      { title: "Map your workflow", body: "We learn how your business actually runs and where the SaaS stack hurts most." },
      { title: "Design the blueprint", body: "A clear plan of the tool, scope and cost — no fluff, no surprises." },
      { title: "Build in sprints", body: "Working software in weeks, shipped in increments you can see and shape." },
      { title: "Own and evolve", body: "You get the keys. We can keep improving it, or hand it over fully — your call." },
    ],
    faqKicker: "// questions",
    faqTitle: "Owning vs. renting — answered",
    faqs: [
      {
        q: "Isn't building custom software more expensive than SaaS?",
        a: "Upfront, sometimes. Over 2–3 years, custom usually wins — especially once SaaS per-seat fees, add-ons and price hikes stack up. And at the end you own an asset instead of a string of receipts.",
      },
      {
        q: "What if my needs change later?",
        a: "That's the whole point. Because you own it, the tool evolves with you — new features, new workflows, new integrations — on your timeline, not a vendor's.",
      },
      {
        q: "Do I need a technical team to run it?",
        a: "No. We host, maintain and support it for you if you want, or hand over full documentation and access if you'd rather run it yourself.",
      },
      {
        q: "Can it replace several tools at once?",
        a: "Often yes. Consolidating 3–5 overlapping subscriptions into one tool that fits perfectly is one of the most common — and most profitable — projects we do.",
      },
      {
        q: "How long until I see something real?",
        a: "Usually a few weeks for a first working version. We build in increments so you're never waiting months to see progress.",
      },
    ],
    ctaTitle: "Stop renting. Start owning.",
    ctaBody:
      "Tell us which SaaS subscriptions are holding your business back. We'll show you what owning the right tool could look like.",
  },

  {
    slug: "custom-crm",
    eyebrow: "// your_crm",
    title: "A CRM that fits you — [[not the other way around.]]",
    subtitle:
      "Salesforce is bloated. HubSpot is rigid. The 'simple' ones are too simple. We build a CRM around your real sales process — every pipeline, field and automation exactly how your team sells.",
    metaTitle: "Custom CRM Development — Better Than Off-the-Shelf | DevCore",
    metaDescription:
      "Generic CRMs force your team to adapt. DevCore builds a custom CRM around your exact pipeline, automations and data — no bloat, no per-seat tax, no compromises.",
    stats: [
      { value: "1", label: "CRM shaped to your process" },
      { value: "0", label: "unused features to pay for" },
      { value: "100%", label: "your data, your rules" },
    ],
    problemKicker: "// the_struggle",
    problemTitle: "Your CRM should help you sell — not slow you down",
    problemBody:
      "Most teams either drown in a CRM that does too much or outgrow one that does too little. So they live in spreadsheets, lose deals in the cracks, and pay for hundreds of features they'll never touch.",
    problemPoints: [
      "Pipelines that don't match how you actually sell",
      "Hours lost to manual data entry and copy-paste",
      "Per-seat pricing that makes growing the team painful",
      "Reports that never answer the question you're asking",
      "Half your process still living in spreadsheets",
    ],
    pillarsKicker: "// what_you_get",
    pillarsTitle: "A CRM built for how your team really sells",
    pillars: [
      {
        icon: "Workflow",
        title: "Your exact pipeline",
        body: "Stages, fields and rules that mirror your real sales motion — not a generic template you have to wrestle into shape.",
      },
      {
        icon: "Zap",
        title: "Automation that fits",
        body: "Follow-ups, handoffs, reminders and data entry handled automatically, exactly where your process needs them.",
      },
      {
        icon: "Database",
        title: "One source of truth",
        body: "Contacts, deals, quotes, emails and history in one place — connected to the rest of your tools, not siloed.",
      },
      {
        icon: "Gauge",
        title: "Reports that answer",
        body: "Dashboards designed around the decisions you actually make, with the metrics that matter to your business.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Off-the-shelf CRM vs. a CRM that's yours",
    compareLeftLabel: "Generic CRM",
    compareRightLabel: "Your custom CRM",
    compareRows: [
      { label: "Pipeline", left: "Fixed templates", right: "Built to your process" },
      { label: "Features", left: "Pay for 100, use 10", right: "Exactly what you need" },
      { label: "Pricing", left: "Per seat per month", right: "Own it, scale free" },
      { label: "Automation", left: "Limited / paywalled", right: "Tailored to you" },
      { label: "Data", left: "Their cloud, their rules", right: "Yours, fully portable" },
      { label: "Integrations", left: "Marketplace roulette", right: "Whatever you run" },
    ],
    processKicker: "// how_we_build",
    processTitle: "From spreadsheet chaos to a CRM you love",
    steps: [
      { title: "Shadow your sales", body: "We map how leads actually move from first touch to closed deal." },
      { title: "Design the pipeline", body: "A blueprint of stages, fields, automations and reports — agreed before we build." },
      { title: "Build & migrate", body: "We ship it in sprints and bring your existing data across cleanly." },
      { title: "Roll out & refine", body: "Your team adopts it fast because it works the way they already do." },
    ],
    faqKicker: "// questions",
    faqTitle: "Custom CRM — answered",
    faqs: [
      {
        q: "Can you migrate our data from our current CRM or spreadsheets?",
        a: "Yes. Clean migration from Salesforce, HubSpot, Pipedrive, Excel or Google Sheets is part of the project, so nothing gets left behind.",
      },
      {
        q: "Will it integrate with our email, calendar and other tools?",
        a: "Absolutely. Email, calendar, invoicing, support, marketing — we connect the CRM to the tools you already rely on.",
      },
      {
        q: "Is a custom CRM really worth it over HubSpot or Salesforce?",
        a: "If your process is non-standard, or per-seat fees are getting painful, yes. You get a tool that fits exactly and an asset you own instead of an ever-growing subscription.",
      },
      {
        q: "How long does it take to build?",
        a: "A focused first version is typically live in a few weeks. We then expand it based on how your team uses it day to day.",
      },
      {
        q: "Can it grow with us?",
        a: "That's the advantage of owning it. New pipelines, teams, regions or product lines get added when you need them — no rebuilding from scratch.",
      },
    ],
    ctaTitle: "Build the CRM your team will actually use.",
    ctaBody:
      "Tell us how you sell. We'll show you a CRM designed around your pipeline — not someone else's template.",
  },

  {
    slug: "build-an-app-without-headaches",
    eyebrow: "// no_headaches",
    title: "Build your app [[without the headaches.]]",
    subtitle:
      "Vague quotes, missed deadlines, agencies that vanish, code you can't read. Building an app shouldn't feel like a gamble. With a senior team and a clear blueprint, it doesn't.",
    metaTitle: "Build an App Without the Headaches — Done by Pros | DevCore",
    metaDescription:
      "Skip the missed deadlines, surprise bills and messy code. DevCore builds your web or mobile app with experienced developers, a clear blueprint and software you actually own.",
    stats: [
      { value: "1", label: "senior team, end to end" },
      { value: "0", label: "surprise invoices" },
      { value: "100%", label: "clean, documented code" },
    ],
    problemKicker: "// the_headache",
    problemTitle: "Why most app projects go sideways",
    problemBody:
      "You've heard the horror stories — or lived them. Budgets that double, timelines that slip, an MVP that's a mess under the hood. The problem usually isn't the idea. It's the lack of a clear plan and a team that actually owns the outcome.",
    problemPoints: [
      "Quotes that balloon halfway through the build",
      "Deadlines that slip with no clear reason",
      "Code so messy the next developer has to start over",
      "Agencies that go quiet the moment it gets hard",
      "An app that technically works but nobody enjoys using",
    ],
    pillarsKicker: "// how_it_should_be",
    pillarsTitle: "What building with pros feels like",
    pillars: [
      {
        icon: "FileText",
        title: "A clear blueprint first",
        body: "Before a line of code, you get a plan: scope, timeline and a fixed, honest budget. No guesswork, no scope creep ambushes.",
      },
      {
        icon: "Code2",
        title: "Experienced developers only",
        body: "Your project isn't handed to juniors. Experienced builders make the right calls early — the ones that save you months later.",
      },
      {
        icon: "Eye",
        title: "Total transparency",
        body: "See progress every week with working software, not status decks. You always know where things stand.",
      },
      {
        icon: "Rocket",
        title: "Polished & yours",
        body: "Fast, beautiful, reliable — and fully owned by you, with clean code and docs so you're never held hostage.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Typical agency vs. building with DevCore",
    compareLeftLabel: "Typical experience",
    compareRightLabel: "With DevCore",
    compareRows: [
      { label: "Pricing", left: "Vague, then it grows", right: "Clear & fixed upfront" },
      { label: "Team", left: "Outsourced juniors", right: "Experienced developers" },
      { label: "Progress", left: "Status decks", right: "Working software weekly" },
      { label: "Code", left: "Messy, undocumented", right: "Clean, documented" },
      { label: "Design", left: "Bolted on at the end", right: "Built in from day one" },
      { label: "After launch", left: "They disappear", right: "We stick around" },
    ],
    processKicker: "// how_we_build",
    processTitle: "A calm, predictable path to launch",
    steps: [
      { title: "Discovery", body: "We pin down what to build, for whom, and why it matters — fast." },
      { title: "Blueprint", body: "Scope, design direction, timeline and a fixed budget you sign off on." },
      { title: "Build in sprints", body: "Working features every week, with your feedback steering each one." },
      { title: "Launch & support", body: "We ship it, watch it land, and keep improving it with you." },
    ],
    faqKicker: "// questions",
    faqTitle: "Building your app — answered",
    faqs: [
      {
        q: "I have an idea but no technical background. Can you still help?",
        a: "Yes — most of our clients aren't technical. We translate your idea into a clear plan and handle every technical decision, explaining things in plain language.",
      },
      {
        q: "Web app, mobile app, or both?",
        a: "All of the above. We build responsive web apps, mobile apps and the technology behind the scenes, and we'll advise on what makes sense for your goals and budget.",
      },
      {
        q: "How do you avoid the budget blowing up?",
        a: "We scope and price the blueprint before building. You approve it, and we build to it in increments — so there are no mid-project surprises.",
      },
      {
        q: "Will I own the code?",
        a: "Completely. You get clean, documented code and full access. No lock-in, no holding your project hostage.",
      },
      {
        q: "What happens after launch?",
        a: "We don't disappear. We monitor, support and keep improving the app as your business grows — for as long as you want us around.",
      },
    ],
    ctaTitle: "Let's build it properly — the first time.",
    ctaBody:
      "Tell us about your app idea. We'll turn it into a clear blueprint with a real plan, timeline and budget.",
  },

  {
    slug: "custom-portal-not-a-builder",
    eyebrow: "// your_portal",
    title: "A portal built your way — [[not from a template.]]",
    subtitle:
      "Website builders and portal templates force your business into their boxes. We build customer, partner and internal portals exactly to your vision — every flow, permission and pixel.",
    metaTitle: "Custom Portal Development — Not a Website Builder | DevCore",
    metaDescription:
      "Stop forcing your business into a template. DevCore builds customer, partner and internal portals exactly to your spec — your flows, your branding, your rules.",
    stats: [
      { value: "0", label: "template constraints" },
      { value: "100%", label: "your brand & flows" },
      { value: "∞", label: "ways it can grow" },
    ],
    problemKicker: "// the_box",
    problemTitle: "Builders give you a box. Your business doesn't fit in it.",
    problemBody:
      "Drag-and-drop builders and portal templates look quick — until you hit the wall. The login flow you need isn't possible. The permissions don't match your org. The branding only goes so far. You end up paying monthly for something that still isn't right.",
    problemPoints: [
      "Rigid templates that fight your real workflows",
      "Permission models that don't match how your teams work",
      "Branding that stops at a logo and a color",
      "Integrations blocked unless you upgrade — again",
      "Monthly fees for a portal that's still 'almost' right",
    ],
    pillarsKicker: "// what_you_get",
    pillarsTitle: "A portal designed entirely around you",
    pillars: [
      {
        icon: "Layout",
        title: "Exactly your vision",
        body: "Every screen, flow and interaction built to your spec — not assembled from whatever blocks a builder happens to offer.",
      },
      {
        icon: "ShieldCheck",
        title: "Roles & permissions that fit",
        body: "Granular access for customers, partners, teams and admins — modelled on your real organization, not a generic preset.",
      },
      {
        icon: "Boxes",
        title: "Connected to everything",
        body: "Your CRM, billing, support, data warehouse — the portal plugs into the systems you already run, with no artificial limits.",
      },
      {
        icon: "Sparkles",
        title: "Branded to the pixel",
        body: "It looks and feels unmistakably yours — a premium product experience, not a recognizable template.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Website builder vs. a custom portal",
    compareLeftLabel: "Builder / template",
    compareRightLabel: "Your custom portal",
    compareRows: [
      { label: "Flows", left: "Whatever fits the blocks", right: "Exactly your spec" },
      { label: "Permissions", left: "Basic presets", right: "Modelled on your org" },
      { label: "Branding", left: "Logo + colors", right: "Pixel-perfect, yours" },
      { label: "Integrations", left: "Limited, paywalled", right: "Anything you run" },
      { label: "Pricing", left: "Monthly, forever", right: "Own it outright" },
      { label: "Growth", left: "You hit the ceiling", right: "No ceiling" },
    ],
    processKicker: "// how_we_build",
    processTitle: "From 'almost right' to exactly right",
    steps: [
      { title: "Define the vision", body: "We map your users, flows and the experience you want them to have." },
      { title: "Design the blueprint", body: "Screens, roles, integrations and scope — agreed before we build." },
      { title: "Build in sprints", body: "A working portal in weeks, refined with your feedback each step." },
      { title: "Launch & evolve", body: "Go live, then grow it — new modules and flows whenever you need them." },
    ],
    faqKicker: "// questions",
    faqTitle: "Custom portals — answered",
    faqs: [
      {
        q: "What kind of portals do you build?",
        a: "Customer portals, partner and reseller portals, client dashboards, internal team portals, membership areas — anything where people log in to get something done.",
      },
      {
        q: "Can it connect to our existing systems?",
        a: "Yes. We integrate with your CRM, billing, support, auth provider and data sources so the portal is part of your stack, not an island.",
      },
      {
        q: "How is this different from a website builder?",
        a: "A builder limits you to its blocks and rules. We build to your exact requirements — custom flows, real permission models and unlimited integrations — and you own the result.",
      },
      {
        q: "Can we add more features later?",
        a: "Always. Because it's custom and yours, new modules, user types and workflows can be added whenever your business needs them.",
      },
      {
        q: "How secure is it?",
        a: "Security is built in from the start — secure login, role-based access rights and data protection, not an afterthought bolted onto a template.",
      },
    ],
    ctaTitle: "Build the portal you actually pictured.",
    ctaBody:
      "Describe the portal in your head. We'll turn it into a blueprint and build it exactly that way.",
  },

  {
    slug: "tools-that-dont-exist-yet",
    eyebrow: "// build_the_unbuilt",
    title: "Build the tool [[that doesn't exist yet.]]",
    subtitle:
      "Sometimes the perfect tool simply isn't on the market — or the ones that are do far too much, or far too little. We build the exact tool your business needs, no more and no less.",
    metaTitle: "Build Custom Tools That Don't Exist on the Market | DevCore",
    metaDescription:
      "Can't find the right tool — or stuck with bloated software you barely use? DevCore builds the exact custom tool your business needs, built to fit and owned by you.",
    stats: [
      { value: "1", label: "tool built to fit" },
      { value: "0", label: "bloat you don't need" },
      { value: "100%", label: "made for your workflow" },
    ],
    problemKicker: "// the_gap",
    problemTitle: "The right tool either doesn't exist — or does too much",
    problemBody:
      "You've searched. Either nothing on the market solves your specific problem, or everything that does comes bundled with overhead, features and pricing you'll never need. So you settle, and your team pays for it in lost time every single day.",
    problemPoints: [
      "No product on the market does the one thing you actually need",
      "The closest options are bloated and overpriced",
      "Manual, repetitive work nobody has time for",
      "A patchwork of spreadsheets and half-working hacks",
      "Hours lost every week to a problem software should solve",
    ],
    pillarsKicker: "// what_you_get",
    pillarsTitle: "The exact tool — nothing more, nothing less",
    pillars: [
      {
        icon: "Target",
        title: "Made to fit",
        body: "Built around the one problem you need solved, the way your team actually works — not an average of everyone else's needs.",
      },
      {
        icon: "Feather",
        title: "Zero bloat",
        body: "No features you'll never use, no overhead you pay for and ignore. Lean, focused and fast.",
      },
      {
        icon: "Zap",
        title: "Automates the grind",
        body: "The repetitive, manual work that eats your week — handled. Your team gets the hours back.",
      },
      {
        icon: "Layers",
        title: "Grows on demand",
        body: "Start with the core tool, then add exactly the capabilities you need, when you need them.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Forcing a market tool vs. building yours",
    compareLeftLabel: "Closest market tool",
    compareRightLabel: "Your custom tool",
    compareRows: [
      { label: "Fit", left: "Close-ish, never exact", right: "Built for the job" },
      { label: "Features", left: "Bloated or missing", right: "Exactly right" },
      { label: "Overhead", left: "Pay for the unused", right: "None" },
      { label: "Workflow", left: "You adapt", right: "It adapts to you" },
      { label: "Cost", left: "Subscription forever", right: "Own it" },
      { label: "Future", left: "Wait on their roadmap", right: "Add what you need" },
    ],
    processKicker: "// how_we_build",
    processTitle: "From 'this should exist' to it existing",
    steps: [
      { title: "Pin the problem", body: "We get crystal clear on the one thing the tool must do brilliantly." },
      { title: "Blueprint it", body: "Scope, design and a fixed budget for a lean, focused tool." },
      { title: "Build the core", body: "A working version in weeks — the essential tool, doing its job." },
      { title: "Sharpen & extend", body: "Refine it in real use, then add capabilities only as you need them." },
    ],
    faqKicker: "// questions",
    faqTitle: "Building the unbuilt — answered",
    faqs: [
      {
        q: "What if I'm not sure exactly what the tool should be?",
        a: "That's normal — and where we help most. We start from the problem and shape the right tool with you, so you don't need a finished spec to begin.",
      },
      {
        q: "Is it worth building something so specific?",
        a: "If the problem costs you real time or money every week, almost always. A focused tool that fits perfectly pays for itself far faster than people expect.",
      },
      {
        q: "Can it use AI?",
        a: "Yes. When it genuinely helps — automating decisions, processing text or data, assisting your team — we build AI in, with sensible safeguards. When it doesn't, we keep it simple.",
      },
      {
        q: "How small can a project be?",
        a: "Small is fine. A single sharp tool that removes one painful bottleneck is one of the highest-ROI things we build.",
      },
      {
        q: "Will I own it?",
        a: "Yes — fully. Clean code, full access, no lock-in. It's your tool and your asset.",
      },
    ],
    ctaTitle: "If it should exist, let's build it.",
    ctaBody:
      "Describe the tool you wish you had. We'll tell you exactly how we'd build it — and own it for you.",
  },

  {
    slug: "combine-your-tools-into-one-suite",
    eyebrow: "// one_suite",
    title: "Many scattered tools. [[One tailored suite.]]",
    subtitle:
      "Your team jumps between a dozen apps, copies data by hand and pays for each one separately. We unite the small tools you actually use into a single, custom suite — one login, one source of truth, built around your workflow.",
    metaTitle: "Combine Multiple Tools Into One Custom Suite | DevCore",
    metaDescription:
      "Stop juggling a dozen disconnected apps. DevCore unifies your small tools into one tailored suite — single login, shared data, no more copy-paste or stacked subscriptions.",
    stats: [
      { value: "1", label: "suite instead of a dozen apps" },
      { value: "0", label: "copy-paste between tools" },
      { value: "100%", label: "connected, shared data" },
    ],
    problemKicker: "// the_sprawl",
    problemTitle: "A dozen apps that don't talk to each other",
    problemBody:
      "Every problem got its own tool, and now your team lives in tab chaos. Data is duplicated across apps, nothing stays in sync, and you pay a separate subscription for each one. The tools were meant to save time — together they quietly waste it.",
    problemPoints: [
      "The same data re-entered into three or four different apps",
      "Endless tab-switching and broken context all day long",
      "Tools that don't sync, so numbers never quite match",
      "A stack of separate subscriptions adding up every month",
      "No single place to see what's actually going on",
    ],
    pillarsKicker: "// what_you_get",
    pillarsTitle: "Everything your team needs, in one place",
    pillars: [
      {
        icon: "Boxes",
        title: "Your tools, unified",
        body: "The handful of small apps you rely on, rebuilt as connected modules in one suite — designed to work together instead of fighting each other.",
      },
      {
        icon: "Database",
        title: "One source of truth",
        body: "Enter data once and it's everywhere it needs to be. No duplicates, no exports, no copy-paste — every module shares the same live data.",
      },
      {
        icon: "Layers",
        title: "One login, one experience",
        body: "A single, consistent interface and one set of permissions for everything — your team learns it once and stops hunting across apps.",
      },
      {
        icon: "Zap",
        title: "Less cost, less overhead",
        body: "Replace a pile of subscriptions and the glue work between them with one tool you own — lower running cost and far less to maintain.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "A patchwork of apps vs. one tailored suite",
    compareLeftLabel: "Scattered tools",
    compareRightLabel: "Your custom suite",
    compareRows: [
      { label: "Data", left: "Duplicated everywhere", right: "One shared source" },
      { label: "Workflow", left: "Constant tab-switching", right: "One connected flow" },
      { label: "Logins", left: "One per app", right: "Single sign-on" },
      { label: "Cost", left: "Many subscriptions", right: "One owned suite" },
      { label: "Integrations", left: "Brittle, breaks often", right: "Built-in, native" },
      { label: "Overview", left: "Scattered, partial", right: "One clear view" },
    ],
    processKicker: "// how_we_build",
    processTitle: "From tool sprawl to one tailored suite",
    steps: [
      { title: "Audit your stack", body: "We map every tool your team uses and where the data actually flows between them." },
      { title: "Design the suite", body: "A blueprint of the unified modules, shared data model and one clean interface — agreed before we build." },
      { title: "Build & migrate", body: "We ship the suite in sprints and bring your data over from the old tools cleanly." },
      { title: "Switch over & extend", body: "Your team moves onto one tool, and we add modules as new needs come up." },
    ],
    faqKicker: "// questions",
    faqTitle: "One suite — answered",
    faqs: [
      {
        q: "Can you really replace all our separate tools?",
        a: "Usually the ones that matter, yes. We focus on the tools your team uses daily and the data that flows between them, rebuilding them as connected modules in one suite. Anything best left to a specialist tool, we integrate instead.",
      },
      {
        q: "What about the tools we still want to keep?",
        a: "No problem. The suite connects to the external tools you want to keep — email, accounting, payments and so on — so everything still flows into one place.",
      },
      {
        q: "Can you migrate our existing data?",
        a: "Yes. Clean migration from your current apps and spreadsheets is part of the project, so nothing gets left behind in the switch.",
      },
      {
        q: "Isn't one big tool risky compared to many small ones?",
        a: "It's the opposite of a fragile patchwork. One well-built, owned suite means fewer breaking integrations, fewer vendors and one place to secure and maintain — far more robust than a dozen apps duct-taped together.",
      },
      {
        q: "Can it grow as we add new needs?",
        a: "Always. New modules and workflows get added to the same suite when you need them — no new subscriptions, no fresh integration headaches.",
      },
    ],
    ctaTitle: "Turn your tool sprawl into one suite.",
    ctaBody:
      "Tell us which apps your team juggles every day. We'll show you how they fit into a single suite built around you.",
  },

  {
    slug: "why-not-vibecode-it-yourself",
    eyebrow: "// vibecoding",
    title: "Why not just [[vibecode it yourself?]]",
    subtitle:
      "AI makes building feel effortless — until the demo has to become a real product. Vibecoding is brilliant for prototypes and dangerous for production. Here's where it breaks, and why a senior team turns your idea into software you can actually trust.",
    metaTitle: "Why Not Just Vibecode It Yourself? The Honest Answer | DevCore",
    metaDescription:
      "AI coding tools make a prototype in an afternoon — but production is a different game. See where vibecoding breaks (security, scaling, maintenance) and when to bring in pros.",
    stats: [
      { value: "1 day", label: "to a demo — months to a real product" },
      { value: "80%", label: "of the work is the invisible 80%" },
      { value: "100%", label: "of liability stays with you" },
    ],
    problemKicker: "// the_trap",
    problemTitle: "The demo works. That's exactly the trap.",
    problemBody:
      "Vibecoding gets you a working screen astonishingly fast — and that first win hides everything underneath. Secure login, reliable data, edge cases, security, performance, payments, compliance: none of it shows up in a happy-path demo, and all of it shows up the moment real users (and attackers) arrive. The gap between 'it runs' and 'I'd bet my business on it' is where most self-built apps quietly fall apart.",
    problemPoints: [
      "Security holes you can't see — exposed data, missing access rules, leaked keys",
      "Code that works once but nobody, including you, can safely change later",
      "Edge cases, errors and weird inputs that crash real-world usage",
      "No tests, no backups, no monitoring — until something breaks in production",
      "Every hour debugging AI output is an hour you're not running your business",
    ],
    pillarsKicker: "// the_reality",
    pillarsTitle: "What vibecoding doesn't put on screen",
    pillars: [
      {
        icon: "ShieldCheck",
        title: "Security & data integrity",
        body: "Secure login, clear access rights, validated inputs and safely protected passwords & keys — the things AI happily skips and attackers happily find. We build them in from line one.",
      },
      {
        icon: "TrendingUp",
        title: "Built to scale",
        body: "A prototype that works for 5 users can collapse at 500. We architect data, queries and infrastructure so growth is a non-event, not a rewrite.",
      },
      {
        icon: "Code2",
        title: "Maintainable foundations",
        body: "Clean, documented, tested code a human can reason about — so the app keeps evolving instead of becoming a black box you're afraid to touch.",
      },
      {
        icon: "Eye",
        title: "Judgement, not just output",
        body: "AI generates code; it doesn't own the outcome. Experienced developers make the calls AI can't — trade-offs, architecture and the decisions that save you months later.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Vibecoding it yourself vs. building with pros",
    compareLeftLabel: "DIY vibecoding",
    compareRightLabel: "Built with DevCore",
    compareRows: [
      { label: "Speed to demo", left: "Hours", right: "Hours", leftPositive: true },
      { label: "Deployment process", left: "Manual, fragile, breaks on push", right: "Automated, with safe rollbacks" },
      { label: "Hosting & infrastructure", left: "Guesswork, no monitoring", right: "Configured, scaled, observed" },
      { label: "Performance", left: "Slows down with real data", right: "Profiled, cached, optimized" },
      { label: "Bugs & edge cases", left: "Surface in production", right: "Caught with tests before launch" },
      { label: "Security", left: "Unknown / hope", right: "Designed in, reviewed, hardened" },
      { label: "Connections to other programs", left: "Brittle, cobbled together", right: "Robust and cleanly documented" },
    ],
    processKicker: "// how_we_build",
    processTitle: "Keep the speed. Lose the risk.",
    steps: [
      { title: "Bring your prototype", body: "Vibecoded something already? Great — it's the perfect spec. We turn it into a real plan." },
      { title: "Blueprint the real thing", body: "Scope, architecture, security and a fixed budget — what production actually requires, no guesswork." },
      { title: "Build it properly", body: "Experienced developers ship working software in sprints, with tests, reviews and the invisible 80% handled." },
      { title: "Launch & own it", body: "You get clean code, full ownership and a tool you can trust with real users — and real money." },
    ],
    faqKicker: "// questions",
    faqTitle: "Vibecoding vs. building it right — answered",
    faqs: [
      {
        q: "Isn't AI good enough to build my whole app now?",
        a: "For prototypes and internal experiments, often yes — and it's genuinely powerful. For production software with real users, payments, data and security, AI is a tool, not a team. It writes code fast but doesn't own the architecture, the trade-offs or the consequences. That's where we come in.",
      },
      {
        q: "I already vibecoded something. Is it wasted?",
        a: "Not at all — it's an asset. A working prototype is the clearest possible brief: it shows exactly what you want. We use it as the blueprint and rebuild it on solid, secure, scalable foundations, often much faster because the thinking is already done.",
      },
      {
        q: "Why pay you when AI is so cheap?",
        a: "You're not paying for typing code — AI made that cheap. You're paying for judgement: the security you can't see, the architecture that won't collapse, the decisions that prevent expensive rewrites. The cheap part was never the bottleneck.",
      },
      {
        q: "Do you use AI too?",
        a: "Absolutely — we'd be slower if we didn't. The difference is we use it under senior supervision, with reviews, tests and standards. You get AI's speed with a professional's accountability, not raw output shipped to production.",
      },
      {
        q: "How do I know when to stop DIY and call you?",
        a: "When real users, real money or sensitive data are involved — or when you're spending more time fighting your code than building your business. If a bug could cost you customers or a breach could cost you trust, it's time.",
      },
    ],
    ctaTitle: "Love the speed? Keep it — without the risk.",
    ctaBody:
      "Send us your vibecoded prototype or just the idea. We'll show you what it takes to make it production-ready — and build it for you.",
  },

  {
    slug: "ai-built-into-your-software",
    eyebrow: "// ai_inside",
    title: "AI that does real work — [[built into your software.]]",
    subtitle:
      "Most teams bolt a chatbot onto a finished product and call it AI. We design intelligence into the core of what we build — reading documents, answering users, spotting trends and sorting the chaos, every single day.",
    metaTitle: "AI Built Into Your Software — Not Bolted On | DevCore",
    metaDescription:
      "Stop bolting a chatbot onto a finished product. DevCore builds AI into the core of your software: PDF analysis, copilots, KPI analytics, scraping and smart organisation.",
    stats: [
      { value: "9", label: "AI capabilities, productised" },
      { value: "24/7", label: "work handled automatically" },
      { value: "0", label: "manual copy-paste left" },
    ],
    problemKicker: "// the_gimmick",
    problemTitle: "A chatbot in the corner isn't an AI strategy",
    problemBody:
      "Slapping a generic assistant onto a finished app looks modern but changes nothing. Your team still retypes data from PDFs, hunts for answers, builds reports by hand and drowns in unsorted items. Real AI removes the work — it doesn't just sit there waiting to be asked.",
    problemPoints: [
      "Hours lost retyping data from PDFs, invoices and contracts",
      "Users digging through docs instead of getting answers",
      "Reports built by hand, always a week out of date",
      "Data scattered across the web, other programs and spreadsheets",
      "Inboxes and queues piling up with nothing auto-sorted",
    ],
    pillarsKicker: "// what_we_build",
    pillarsTitle: "Nine ways AI does real work in your product",
    pillars: [
      {
        icon: "FileText",
        title: "Document & PDF analysis",
        body: "Drop in contracts, invoices or reports and get clean, structured data back — no manual typing, no copy-paste, no errors.",
      },
      {
        icon: "Sparkles",
        title: "Integrated copilot & chatbot",
        body: "A context-aware assistant inside your product that answers questions, drafts content and takes real actions for your users.",
      },
      {
        icon: "Gauge",
        title: "KPI & data analytics",
        body: "Live dashboards that turn raw numbers into trends, alerts and decisions your team can act on immediately.",
      },
      {
        icon: "Database",
        title: "Scraping & data collection",
        body: "Pull data from the web, other programs and files, then organise it into one clean, reliable source you can trust.",
      },
      {
        icon: "Workflow",
        title: "Smart organisation",
        body: "Auto-tag, route and sort incoming items so nothing gets lost and your team stays focused on what matters.",
      },
      {
        icon: "Eye",
        title: "Intelligent search & retrieval",
        body: "Semantic search across every document, ticket and record — your team finds the right answer in seconds, no exact keywords needed.",
      },
      {
        icon: "Feather",
        title: "Drafting & content generation",
        body: "Auto-draft emails, replies, summaries and reports in your tone, ready for a human to approve and send in one click.",
      },
      {
        icon: "TrendingUp",
        title: "Forecasting & anomaly detection",
        body: "Predict demand, churn and risk — and get alerted the moment a number drifts out of the ordinary.",
      },
      {
        icon: "Target",
        title: "Automated triage & routing",
        body: "Read incoming requests, understand the intent, and route or escalate them to the right person or workflow instantly.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Bolted-on AI vs. AI built into the core",
    compareLeftLabel: "Bolted-on chatbot",
    compareRightLabel: "AI in the core",
    compareRows: [
      { label: "Role", left: "Sits and waits to be asked", right: "Does work automatically" },
      { label: "Documents", left: "You still retype data", right: "Parsed into clean data" },
      { label: "Answers", left: "Generic, off-context", right: "Grounded in your data" },
      { label: "Reporting", left: "Manual, always late", right: "Live and proactive" },
      { label: "Data", left: "Scattered everywhere", right: "Collected & normalised" },
      { label: "Value", left: "A demo feature", right: "Daily real output" },
    ],
    processKicker: "// how_we_build",
    processTitle: "From AI hype to AI that earns its keep",
    steps: [
      { title: "Find the real work", body: "We map where your team loses hours — the typing, searching and sorting AI can actually remove." },
      { title: "Design the blueprint", body: "Which AI capabilities, where they live in your product, and the safeguards that keep them safe." },
      { title: "Build it in", body: "We engineer the intelligence into the core of your software in sprints — tested, not a gimmick." },
      { title: "Measure & sharpen", body: "We track the work it saves and keep tuning accuracy, so the value compounds over time." },
    ],
    faqKicker: "// questions",
    faqTitle: "AI in your software — answered",
    faqs: [
      {
        q: "Is this just a ChatGPT wrapper?",
        a: "No. A wrapper answers questions; we build AI that does work — parsing documents into structured data, generating live analytics, collecting and normalising data, searching across everything, forecasting, drafting and routing items automatically. The chatbot is just one of nine capabilities, and even that is grounded in your own data.",
      },
      {
        q: "Is our data safe when you add AI?",
        a: "Yes. We design with strict safeguards: scoped access, no training on your private data, input validation and clear boundaries on what the AI can and can't do. Security is built in from the blueprint, not patched on later.",
      },
      {
        q: "How accurate is AI document and PDF analysis?",
        a: "Very, for structured documents like invoices, contracts and reports — and we add validation and human-in-the-loop checks where accuracy is critical, so you get speed without blind trust.",
      },
      {
        q: "Can you add AI to software we already have?",
        a: "Often yes. We can integrate these capabilities into an existing product, or build them into something new. We start by finding the highest-value place to add intelligence and go from there.",
      },
      {
        q: "How long until the AI is doing real work?",
        a: "A focused first capability is usually live in a few weeks. We ship in increments, so you see real output early instead of waiting months for a big-bang launch.",
      },
    ],
    ctaTitle: "Put AI to work — don't just bolt it on.",
    ctaBody:
      "Tell us where your team loses the most time. We'll show you exactly where AI built into your software would do real work.",
  },
  {
    slug: "custom-software-fixed-price",
    eyebrow: "// fixed_price",
    title: "Custom software at a [[fixed price.]]",
    subtitle:
      "Getting your own software built sounds expensive and complicated. It isn't. One fixed price, agreed upfront, and one dedicated contact from the first call to launch — no hourly surprises, no agency runaround.",
    metaTitle: "Custom Software at a Fixed Price — One Contact | DevCore",
    metaDescription:
      "Think custom software is too expensive or complicated? It's not. DevCore builds it at a fixed price agreed upfront, with one dedicated contact from start to launch.",
    stats: [
      { value: "€ fixed", label: "price agreed before we start" },
      { value: "1", label: "dedicated contact, start to finish" },
      { value: "0", label: "hourly surprises" },
    ],
    problemKicker: "// the_fear",
    problemTitle: "Custom software has a reputation — and it scares people off",
    problemBody:
      "Most owners assume building their own software means a blank-cheque budget, a project that drags on for a year, and a rotating cast of strangers who never quite get your business. That's how big agencies work. It's not how we work.",
    problemPoints: [
      "Open-ended quotes that grow every month",
      "No idea what the final bill will actually be",
      "A different account manager every few weeks",
      "Tech jargon instead of straight answers",
      "The feeling it's only for big companies with big budgets",
    ],
    pillarsKicker: "// how_we_do_it",
    pillarsTitle: "Predictable, personal, and built for smaller businesses",
    pillars: [
      {
        icon: "FileText",
        title: "One fixed price, agreed first",
        body: "Before we write a line of code, you get a clear scope and one fixed price. You sign off — and that's exactly what you pay. No hourly meter running in the background.",
      },
      {
        icon: "Eye",
        title: "One contact who knows you",
        body: "You talk to the same person from the first call to launch. They know your business, your goals and your project — no being passed around, no re-explaining yourself.",
      },
      {
        icon: "Gauge",
        title: "Clear, not complicated",
        body: "We translate the tech into plain language and a simple plan. You always understand what's being built, why, and what it costs.",
      },
      {
        icon: "Rocket",
        title: "Sized to your business",
        body: "We scope to your real needs and budget — start lean, prove the value, then grow. Custom software isn't only for corporations anymore.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Typical agency vs. a fixed price with DevCore",
    compareLeftLabel: "Typical agency",
    compareRightLabel: "DevCore",
    compareRows: [
      { label: "Pricing", left: "Hourly, open-ended", right: "Fixed, agreed upfront" },
      { label: "Contact", left: "Rotating account managers", right: "One person, start to end" },
      { label: "Budget", left: "Grows mid-project", right: "What you sign is what you pay" },
      { label: "Language", left: "Tech jargon", right: "Plain and clear" },
      { label: "Project size", left: "Built for big budgets", right: "Sized to your business" },
      { label: "Ownership", left: "Locked to them", right: "The code is yours" },
    ],
    processKicker: "// how_it_works",
    processTitle: "From idea to fixed quote in days, not months",
    steps: [
      { title: "Free first call", body: "Tell us what you need. We listen and ask the right questions — no obligation, no jargon." },
      { title: "Fixed quote", body: "You get a clear scope and one fixed price, in plain language, before anything is built." },
      { title: "We build it", body: "Your dedicated contact keeps you posted every week with working software you can actually see." },
      { title: "Launch & support", body: "We ship it, hand over the keys, and stay reachable — same person, same number." },
    ],
    faqKicker: "// questions",
    faqTitle: "Fixed-price software — answered",
    faqs: [
      {
        q: "Is custom software really affordable for a small business?",
        a: "Yes, more often than people think. We scope to your real needs and budget — we can start with a focused first version that proves its value, then grow from there. You're not paying corporate agency rates.",
      },
      {
        q: "What exactly does 'fixed price' mean?",
        a: "We agree the scope and the price before a line of code is written. As long as the scope doesn't change, that's the price you pay — no hourly meter, no surprise invoice at the end.",
      },
      {
        q: "What if I'm not technical at all?",
        a: "Perfect — most of our clients aren't. You explain your business in plain words, and we handle every technical decision and explain it back simply.",
      },
      {
        q: "Who will I be dealing with?",
        a: "One dedicated contact, from the first call through to launch and beyond. They know your project inside out, so you never have to re-explain yourself.",
      },
      {
        q: "How fast can we start?",
        a: "The first call can happen this week, and you can have a fixed quote within days. Building usually starts shortly after you approve it.",
      },
    ],
    ctaTitle: "Get a fixed price — before you commit to anything.",
    ctaBody:
      "Tell us what you want to build. You'll get a clear plan and one fixed price, with no obligation and no jargon.",
    adLanding: true,
  },
  {
    slug: "your-idea-we-build",
    eyebrow: "// you_explain_we_build",
    title: "You've got the idea. [[We've got the tech team.]]",
    subtitle:
      "You don't need to be technical to get software built. You explain what you want, we build it — clear, planned and predictable, with no in-house developers required.",
    metaTitle: "You Explain, We Build — Software Without a Tech Team | DevCore",
    metaDescription:
      "Got a software idea but no tech team? DevCore is your development team. You explain it, we build it — clear, planned and predictable, no technical background needed.",
    stats: [
      { value: "0", label: "developers you need to hire" },
      { value: "1", label: "team that handles all the tech" },
      { value: "100%", label: "yours when it's done" },
    ],
    problemKicker: "// the_gap",
    problemTitle: "A great idea is worth nothing without a team to build it",
    problemBody:
      "You can see exactly what the software should do. The problem is the gap between the idea in your head and working software — and hiring developers, managing them and judging whether they're any good is a job in itself. That's the part we take off your plate.",
    problemPoints: [
      "No technical co-founder or in-house developers",
      "No way to judge if a freelancer is actually good",
      "The idea stuck in your head (or a slide deck) for months",
      "Quotes you can't evaluate and timelines you can't trust",
      "The fear of paying for something that never ships",
    ],
    pillarsKicker: "// what_we_handle",
    pillarsTitle: "You bring the vision. We bring everything else.",
    pillars: [
      {
        icon: "Eye",
        title: "You explain, in plain words",
        body: "No specs, no diagrams needed. You describe what you want it to do, and we turn it into a clear plan you can actually read.",
      },
      {
        icon: "Code2",
        title: "We are your tech team",
        body: "Design, development, infrastructure, testing — a full senior team, without hiring, managing or paying salaries.",
      },
      {
        icon: "FileText",
        title: "Clear and planned",
        body: "You get scope, timeline and price upfront, then working software every week. You always know where things stand.",
      },
      {
        icon: "Rocket",
        title: "Predictable, not a gamble",
        body: "We build in small steps you can see and steer, so it's never months of silence followed by a surprise.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Hiring a team vs. letting us build it",
    compareLeftLabel: "Hiring developers",
    compareRightLabel: "You explain, we build",
    compareRows: [
      { label: "Setup", left: "Recruit, vet, manage", right: "One call to start" },
      { label: "Skill", left: "Hope they're good", right: "Proven senior team" },
      { label: "Cost", left: "Salaries & overhead", right: "One agreed price" },
      { label: "Your role", left: "Manage developers", right: "Just explain & approve" },
      { label: "Risk", left: "Carry it alone", right: "We carry it with you" },
      { label: "Result", left: "If it ships", right: "Working software you own" },
    ],
    processKicker: "// how_it_works",
    processTitle: "From 'I have an idea' to live software",
    steps: [
      { title: "Tell us the idea", body: "A relaxed first call where you explain what you want — in everyday language, no tech needed." },
      { title: "We plan it", body: "We turn it into a clear blueprint: what it does, how long, what it costs. You approve it." },
      { title: "We build it", body: "Your software is built in weekly increments you can see, try and shape as we go." },
      { title: "It's yours", body: "We launch it, hand it over, and stick around to support and grow it." },
    ],
    faqKicker: "// questions",
    faqTitle: "You explain, we build — answered",
    faqs: [
      {
        q: "I'm not technical at all. Is that a problem?",
        a: "Not at all — it's the norm for us. You explain your idea in plain language and we handle every technical decision, keeping you in the loop in words you understand.",
      },
      {
        q: "Do I need to write a spec or requirements document?",
        a: "No. A conversation is enough to start. We turn what you tell us into a clear blueprint and refine it with you — you don't need to know the jargon.",
      },
      {
        q: "How do I know the software will actually get built?",
        a: "Because you see it growing every week, not at the end. We build in small, visible steps, so progress is real and you're never left guessing.",
      },
      {
        q: "Will I own it, or are we tied to you?",
        a: "You own it completely — the code, the data, everything. We can keep supporting it if you want, but you're never locked in.",
      },
      {
        q: "What does it cost and how long does it take?",
        a: "You get a clear price and timeline upfront, before we build. A focused first version is usually live in a few weeks.",
      },
    ],
    ctaTitle: "Tell us the idea. We'll handle the tech.",
    ctaBody:
      "Describe the software you wish existed. We'll turn it into a clear plan — and then into the real thing.",
    adLanding: true,
  },

  {
    slug: "custom-software-development",
    eyebrow: "// built_for_you",
    title: "Custom software development — [[built around your processes.]]",
    subtitle:
      "Standard software forces your company to adapt. Custom software does the opposite: every screen, rule and integration follows the way you already work — and the result belongs to you.",
    metaTitle: "Custom Software Development — Built to Your Processes | DevCore",
    metaDescription:
      "Have custom software developed that fits your processes exactly: web apps, portals, CRMs and internal tools. Transparent price ranges, clear specification, full ownership. DevCore.",
    stats: [
      { value: "3-16", label: "weeks to launch, by project type" },
      { value: "100%", label: "code & data ownership" },
      { value: "0", label: "per-seat licence fees" },
    ],
    problemKicker: "// the_gap",
    problemTitle: "Off-the-shelf software covers 70%. The other 30% costs you every day.",
    problemBody:
      "Spreadsheets next to the CRM. Copy-paste between five tools. A process that only works because one person knows all the workarounds. That gap isn't a training problem — it's the software not matching the business.",
    problemPoints: [
      "Manual double entry between systems that don't talk",
      "Processes bent to fit the tool instead of the other way round",
      "Reporting that takes hours because the data lives in five places",
      "Licence costs that rise with every new colleague",
      "Critical know-how stuck in spreadsheets and inboxes",
    ],
    pillarsKicker: "// what_you_get",
    pillarsTitle: "Custom software solutions that mirror your business",
    pillars: [
      {
        icon: "Workflow",
        title: "Your processes, one-to-one",
        body: "We model how your company actually works — roles, approvals, exceptions — instead of squeezing it into a generic template.",
      },
      {
        icon: "Boxes",
        title: "Connected to your stack",
        body: "ERP, CRM, accounting, e-mail, DATEV, warehouse — the new system plugs into what you already run, so data flows once.",
      },
      {
        icon: "FileText",
        title: "Clear specification, predictable price",
        body: "In a concept phase we work out requirements, roles and integrations in detail. Then specification, timeline and price are fixed — before development starts.",
      },
      {
        icon: "Lock",
        title: "You own everything",
        body: "Source code, data, infrastructure. No lock-in, no per-seat pricing, no vendor deciding your roadmap.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Standard software vs. custom development",
    compareLeftLabel: "Off-the-shelf tool",
    compareRightLabel: "Custom software",
    compareRows: [
      { label: "Fit to your process", left: "You adapt", right: "It adapts" },
      { label: "Cost model", left: "Per seat, forever", right: "One-off investment" },
      { label: "Integrations", left: "Whatever is offered", right: "Whatever you need" },
      { label: "Data", left: "On someone's platform", right: "Yours" },
      { label: "Roadmap", left: "Decided elsewhere", right: "Decided by you" },
      { label: "Scaling", left: "Gets more expensive", right: "Gets more valuable" },
    ],
    processKicker: "// how_we_build",
    processTitle: "From requirement to running system",
    steps: [
      { title: "Free blueprint", body: "Describe your idea — you get a roadmap, tech stack and rough specification at no cost." },
      { title: "Concept phase & fixed price", body: "Together we define workflows, roles and integrations in detail. The result is a binding specification with timeline and fixed price." },
      { title: "Build in sprints", body: "You see working software every two weeks and steer it while it's being built." },
      { title: "Launch & evolve", body: "Go live, then extend module by module as the business grows." },
    ],
    faqKicker: "// questions",
    faqTitle: "Custom software development — answered",
    faqs: [
      {
        q: "What does custom software cost?",
        a: "It depends on scope. Typical ranges: internal tools from about €15,000, customer portals €30,000–55,000, custom CRMs €35,000–70,000, SaaS platforms (MVP) €50,000–95,000. We quote a binding fixed price after a joint concept phase, once the scope is truly settled. All ranges are on our pricing page.",
      },
      {
        q: "How long does development take?",
        a: "Small tools usually go live in three to six weeks, portals and CRMs in six to 14 weeks, a SaaS platform (MVP) in ten to 16 weeks. We release in sprints, so you work with real software early instead of waiting months.",
      },
      {
        q: "What kinds of systems do you build?",
        a: "Web applications, customer and partner portals, CRMs, ERP extensions, internal tools, dashboards, booking and quoting systems, and AI-supported workflows.",
      },
      {
        q: "Can it integrate with our existing systems?",
        a: "Yes. We connect to ERP, CRM, accounting, e-mail, payment and warehouse systems via API so data is entered once and used everywhere.",
      },
      {
        q: "Who owns the source code?",
        a: "You do — completely. Code, data and infrastructure are yours, including documentation, so you are never dependent on a single provider.",
      },
      {
        q: "How do we start?",
        a: "With the free blueprint: describe your idea in a few sentences and receive a roadmap, tech stack and rough specification. No obligation.",
      },
    ],
    ctaTitle: "Get your software specification — free.",
    ctaBody:
      "Describe your requirement in a few sentences. You get a roadmap, tech stack and rough specification you can act on — with or without us.",
  },

  {
    slug: "software-development-agency",
    eyebrow: "// your_partner",
    title: "A software agency that [[ships — not just presents.]]",
    subtitle:
      "No account managers between you and the developers. A small senior team, fixed scope, fixed price and working software every two weeks. That's the whole model.",
    metaTitle: "Software Development Agency for Custom Business Software | DevCore",
    metaDescription:
      "Looking for a software agency? DevCore builds custom business software with a senior team, fixed price and releases every two weeks. Free blueprint to start.",
    stats: [
      { value: "2", label: "weeks per release" },
      { value: "1", label: "direct contact, no layers" },
      { value: "0", label: "surprise invoices" },
    ],
    problemKicker: "// the_agency_problem",
    problemTitle: "Most agency projects fail before a line of code is written.",
    problemBody:
      "A polished pitch, then juniors on delivery. Requirements that shift into change requests. Budgets that double. The result: a system nobody wanted to own. The fix isn't a bigger agency — it's a smaller, accountable one.",
    problemPoints: [
      "Senior people in the pitch, juniors in delivery",
      "Every clarification turns into a paid change request",
      "Months of silence, then a demo that misses the point",
      "Nobody can explain the code once the project ends",
      "Hourly billing with no ceiling",
    ],
    pillarsKicker: "// how_we_work",
    pillarsTitle: "What working with us actually looks like",
    pillars: [
      {
        icon: "Target",
        title: "Direct to the builders",
        body: "You talk to the people writing the code. No account layer, no telephone game, no lost context.",
      },
      {
        icon: "FileText",
        title: "Written spec, fixed price",
        body: "Scope, timeline and price agreed up front. Changes are discussed openly, not weaponised as change requests.",
      },
      {
        icon: "Gauge",
        title: "Working software every 2 weeks",
        body: "You can click through progress continuously and correct course early — the cheapest moment to change anything.",
      },
      {
        icon: "ShieldCheck",
        title: "Handover, not hostage",
        body: "Documented code, your repositories, your infrastructure. You can continue with us, in-house or elsewhere.",
      },
    ],
    compareKicker: "// side_by_side",
    compareTitle: "Classic agency vs. DevCore",
    compareLeftLabel: "Classic agency",
    compareRightLabel: "DevCore",
    compareRows: [
      { label: "Contact", left: "Account manager", right: "The developers" },
      { label: "Pricing", left: "Hourly, open-ended", right: "Fixed, agreed up front" },
      { label: "Team", left: "Seniors pitch, juniors build", right: "Seniors build" },
      { label: "Progress", left: "Milestone demos", right: "Every two weeks" },
      { label: "Code", left: "Their repo, their rules", right: "Yours, documented" },
      { label: "Scope changes", left: "Change request", right: "Conversation" },
    ],
    processKicker: "// engagement",
    processTitle: "How a project runs with us",
    steps: [
      { title: "Free blueprint", body: "You describe the project, we return a roadmap, tech stack and rough specification." },
      { title: "Intro call", body: "30 minutes on feasibility, risks and what we would do differently." },
      { title: "Fixed offer", body: "Binding scope, timeline and price — you decide with full information." },
      { title: "Build & handover", body: "Releases every two weeks, then documented handover and optional support." },
    ],
    faqKicker: "// questions",
    faqTitle: "Working with a software agency — answered",
    faqs: [
      {
        q: "What kind of projects do you take on?",
        a: "Custom business software: web applications, customer and partner portals, CRMs, internal tools, integrations and AI-supported workflows. Typically for companies between 10 and 500 employees.",
      },
      {
        q: "Do you work remotely or on site?",
        a: "Primarily remote with structured, regular calls. On-site workshops are possible for kick-off or complex process mapping.",
      },
      {
        q: "How do you handle changing requirements?",
        a: "Requirements always change. We plan in sprints so changes land in the next one instead of becoming a contractual fight.",
      },
      {
        q: "What happens after launch?",
        a: "You choose: a support and further-development agreement, or a full handover with documentation so your team continues alone.",
      },
      {
        q: "Why is a small team an advantage?",
        a: "Fewer handovers, no internal escalation paths, senior people on the actual work. That's why a first release takes weeks instead of quarters.",
      },
    ],
    ctaTitle: "Let's look at your project — free.",
    ctaBody:
      "Describe what you want to build. You get a roadmap, tech stack and rough specification back — then you decide whether we build it.",
  },
];

import { leadMagnets_de } from "./leadMagnets.de";
import { leadMagnets_el } from "./leadMagnets.el";

const byLang: Record<string, LeadMagnet[]> = {
  en: leadMagnets,
  de: leadMagnets_de,
  el: leadMagnets_el,
};

export const getLeadMagnet = (slug?: string, lang: string = "en") =>
  (byLang[lang] ?? leadMagnets).find((p) => p.slug === slug) ??
  leadMagnets.find((p) => p.slug === slug);

export const leadMagnetSlugs = leadMagnets.map((p) => p.slug);
